/**
 * Image capabilities loader (Phase 3 — Task 3.2).
 *
 * Fetches the image-model capability contract from the agent-api proxy
 * (`GET /v1/images/models`), version-asserts it, and answers capability
 * questions so the agent-config UI can drive size/quality/aspect candidate
 * lists from the contract instead of hardcoded literals.
 *
 * Fail loud (spec §4.4 / CLAUDE.md rule 12): a schema_version mismatch throws —
 * the loader never silently uses a stale/partial contract.
 */

export const EXPECTED_SCHEMA_VERSION = '1';

export type SizingMode = 'discrete_size' | 'constrained_size' | 'aspect_ratio';

export interface SizingConstraints {
  long_edge_max?: number | null;
  multiple_of?: number | null;
  max_ratio?: number | null;
  min_ratio?: number | null;
  pixel_min?: number | null;
  pixel_max?: number | null;
}

export interface ModelSizing {
  mode?: SizingMode;
  sizes?: string[];
  aspect_ratios?: string[];
  fixed_resolution?: string | null;
  constraints?: SizingConstraints | null;
}

export interface ModelCapability {
  id: string;
  aliases: string[];
  vendor: string;
  display_name: string;
  operations: string[];
  sizing: ModelSizing;
  quality_options: string[];
  output_formats: string[];
  supports_transparency: boolean;
  max_images_out: number;
  cost_per_image: number;
}

export interface CapabilityContract {
  schema_version: string;
  models: ModelCapability[];
}

export class ContractVersionError extends Error {
  constructor(version: unknown) {
    super(
      `image capability contract schema_version=${JSON.stringify(version)}, ` +
        `expected ${JSON.stringify(EXPECTED_SCHEMA_VERSION)} — refusing to use a mismatched contract`
    );
    this.name = 'ContractVersionError';
  }
}

/**
 * Normalize a model record (contract shape or the flattened API row) into a
 * ModelCapability. The agent-api endpoint flattens sizing onto
 * sizes/aspect_ratios but ALSO exposes the discriminated `sizing` object
 * (Phase 3 extension) — prefer the latter when present.
 */
function toCapability(raw: Record<string, unknown>): ModelCapability {
  const id = String(raw.id ?? raw.model_id ?? '');
  const sizingRaw = (raw.sizing ?? {}) as ModelSizing;
  const sizing: ModelSizing = {
    mode: sizingRaw.mode,
    sizes: sizingRaw.sizes ?? (raw.sizes as string[] | undefined) ?? [],
    aspect_ratios: sizingRaw.aspect_ratios ?? (raw.aspect_ratios as string[] | undefined) ?? [],
    fixed_resolution: sizingRaw.fixed_resolution ?? null,
    constraints: sizingRaw.constraints ?? null,
  };
  return {
    id,
    aliases: (raw.aliases as string[] | undefined) ?? [],
    vendor: String(raw.vendor ?? ''),
    display_name: String(raw.display_name ?? id),
    operations: (raw.operations as string[] | undefined) ?? [],
    sizing,
    quality_options: (raw.quality_options as string[] | undefined) ?? [],
    output_formats: (raw.output_formats as string[] | undefined) ?? [],
    supports_transparency: Boolean(raw.supports_transparency),
    max_images_out: Number(raw.max_images_out ?? raw.max_n ?? 1),
    cost_per_image: Number(raw.cost_per_image ?? 0),
  };
}

export class ImageCapabilities {
  private byId = new Map<string, ModelCapability>();
  private aliasToId = new Map<string, string>();
  readonly schemaVersion: string;

  private constructor(schemaVersion: string, models: ModelCapability[]) {
    this.schemaVersion = schemaVersion;
    for (const cap of models) {
      this.byId.set(cap.id, cap);
      this.aliasToId.set(cap.id, cap.id);
      for (const alias of cap.aliases) this.aliasToId.set(alias, cap.id);
    }
  }

  /**
   * Build from a payload carrying a top-level `schema_version` (the frozen
   * contract or the agent-api list response). Throws on version mismatch.
   */
  static fromPayload(payload: {
    schema_version?: unknown;
    models?: Array<Record<string, unknown>>;
  }): ImageCapabilities {
    const version = payload?.schema_version;
    if (version !== EXPECTED_SCHEMA_VERSION) {
      throw new ContractVersionError(version);
    }
    const models = (payload.models ?? []).map(toCapability);
    return new ImageCapabilities(EXPECTED_SCHEMA_VERSION, models);
  }

  /** Fetch the contract from agent-api and version-assert it. Fail loud.
   * Lazy-imports the API client so this module stays unit-testable without the
   * full app env (supabase config) loaded. */
  static async load(): Promise<ImageCapabilities> {
    const { baseApiClient } = await import('@/lib/api/client');
    const resp = await baseApiClient.get('/v1/images/models');
    return ImageCapabilities.fromPayload(resp.data);
  }

  resolve(modelId: string): string {
    return this.aliasToId.get(modelId) ?? modelId;
  }

  has(modelId: string): boolean {
    return this.byId.has(this.resolve(modelId));
  }

  get(modelId: string): ModelCapability | undefined {
    return this.byId.get(this.resolve(modelId));
  }

  all(): ModelCapability[] {
    return Array.from(this.byId.values());
  }

  /** The model's sizing mode, or undefined if the model is unknown. */
  sizingMode(modelId: string): SizingMode | undefined {
    return this.get(modelId)?.sizing.mode;
  }

  /** Legal sizes (size modes). Empty for aspect_ratio models / unknown ids. */
  legalSizes(modelId: string): string[] {
    const cap = this.get(modelId);
    if (!cap || cap.sizing.mode === 'aspect_ratio') return [];
    return cap.sizing.sizes ?? [];
  }

  /** Legal aspect ratios (aspect_ratio mode). Empty otherwise. */
  legalAspect(modelId: string): string[] {
    const cap = this.get(modelId);
    if (!cap || cap.sizing.mode !== 'aspect_ratio') return [];
    return cap.sizing.aspect_ratios ?? [];
  }

  /** Legal quality options. Empty for models with no quality control. */
  legalQuality(modelId: string): string[] {
    return this.get(modelId)?.quality_options ?? [];
  }

  /**
   * Validate a stored selection. Returns human-readable errors (empty = legal).
   * Mirrors agent-api's ImageCapabilities.validate_selection (fail loud, spec I4).
   */
  validateSelection(
    modelId: string,
    opts: { size?: string | null; aspect?: string | null; quality?: string | null } = {}
  ): string[] {
    const errors: string[] = [];
    const cap = this.get(modelId);
    if (!cap) {
      errors.push(`unknown image model '${modelId}' (not in capability contract)`);
      return errors;
    }
    const { size, aspect, quality } = opts;
    const mode = cap.sizing.mode;

    if (mode === 'aspect_ratio') {
      if (size) {
        errors.push(
          `model '${cap.id}' uses aspect_ratio sizing and accepts no image size (got '${size}')`
        );
      }
      if (aspect != null && aspect !== '') {
        const legal = cap.sizing.aspect_ratios ?? [];
        if (!legal.includes(aspect)) {
          errors.push(
            `aspect_ratio '${aspect}' is not legal for model '${cap.id}' (legal: ${legal.join(', ') || 'none'})`
          );
        }
      }
    } else if (size != null && size !== '') {
      const legalSizes = cap.sizing.sizes ?? [];
      if (!legalSizes.includes(size) && !satisfiesConstraints(size, cap.sizing.constraints)) {
        errors.push(
          `image size '${size}' is not legal for model '${cap.id}' (legal: ${legalSizes.join(', ') || 'none'})`
        );
      }
    }

    if (quality != null && quality !== '' && cap.quality_options.length > 0) {
      if (!cap.quality_options.includes(quality)) {
        errors.push(
          `quality '${quality}' is not legal for model '${cap.id}' (legal: ${cap.quality_options.join(', ')})`
        );
      }
    }
    return errors;
  }
}

function satisfiesConstraints(size: string, constraints?: SizingConstraints | null): boolean {
  if (!constraints) return false;
  if (!size.toLowerCase().includes('x')) return false;
  const [wStr, hStr] = size.toLowerCase().split('x', 2);
  const width = Number.parseInt(wStr, 10);
  const height = Number.parseInt(hStr, 10);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return false;

  const { long_edge_max, multiple_of, max_ratio, min_ratio, pixel_min, pixel_max } = constraints;
  if (long_edge_max != null && Math.max(width, height) > long_edge_max) return false;
  if (multiple_of && (width % multiple_of !== 0 || height % multiple_of !== 0)) return false;
  const pixels = width * height;
  if (pixel_min != null && pixels < pixel_min) return false;
  if (pixel_max != null && pixels > pixel_max) return false;
  const longOverShort = Math.max(width, height) / Math.min(width, height);
  const shortOverLong = Math.min(width, height) / Math.max(width, height);
  if (max_ratio != null && longOverShort > max_ratio) return false;
  if (min_ratio != null && shortOverLong < min_ratio) return false;
  return true;
}
